import { useLoaderData } from '@remix-run/react'
import { PublicKey } from '@solana/web3.js'
import {
	type ActionFunctionArgs,
	type LoaderFunction,
	type LoaderFunctionArgs,
	json,
	redirect,
} from '@remix-run/node'
import Home from '#app/components/home/home.tsx'
import { getNFTFromCache } from '#app/server/db.server.ts'
import { GeneralErrorBoundary } from '#app/components/common/errorBoundary.tsx'
import { chainOptions } from '#app/utils/misc.tsx'

export const loader: LoaderFunction = async ({
	request,
	params,
}: LoaderFunctionArgs) => {
	let recordSectionAttestations = []
	let profileSectionContent = []
	let featuredContent = null

	if (import.meta.env.VITE_HOME_RECORD_ATTESTATIONS_CIDS) {
		recordSectionAttestations =
			import.meta.env.VITE_HOME_RECORD_ATTESTATIONS_CIDS.split(',')
	}

	if (import.meta.env.VITE_HOME_PROFILE_CONTENT) {
		try {
			profileSectionContent = JSON.parse(
				import.meta.env.VITE_HOME_PROFILE_CONTENT,
			)
		} catch (error) {
			console.error('Failed to parse HOME_PROFILE_CONTENT:', error)
		}
	}

	if (import.meta.env.VITE_HOME_FEATURED) {
		try {
			featuredContent = JSON.parse(import.meta.env.VITE_HOME_FEATURED)
		} catch (error) {
			console.error('Failed to parse HOME_FEATURED:', error)
		}
	}

	return {
		recordSectionAttestations,
		profileSectionContent,
		featuredContent,
	}
}

function getStringsFromLink(link: string) {
	const url = new URL(link)
	const paths = url.pathname.split('/')
	let contractAddressFromLink = ''
	let tokenIdFromLink = ''
	let tokenAddressFromLink = ''
	let error = ''
	let chainFromLink = ''

	if (url.hostname == 'exchange.art') {
		const lastPath = paths[paths.length - 1]
		if (lastPath.indexOf('-') > -1) {
			const splitLastPath = lastPath.split('-')
			contractAddressFromLink = splitLastPath[0]
			tokenIdFromLink = splitLastPath[1]
			chainFromLink = 'ethereum'
		} else {
			const isValidAddress = PublicKey.isOnCurve(lastPath)

			if (isValidAddress) {
				tokenAddressFromLink = lastPath
				chainFromLink = 'solana'
			} else {
				error = "Sorry, this doesn't appear to be a valid exchange.art url"
			}
		}
	} else if (url.hostname == 'solana.fm') {
		chainFromLink = 'solana'
		const lastPath = paths[paths.length - 1]
		const isValidAddress = PublicKey.isOnCurve(lastPath)
		if (isValidAddress) {
			tokenAddressFromLink = lastPath
		} else {
			error = "Sorry, this doesn't appear to be a valid solana.fm url"
		}
	} else if (url.hostname == 'opensea.io') {
		if (
			paths[2] === 'ethereum' ||
			paths[2] === 'base' ||
			paths[2] === 'matic'
		) {
			if (paths[2] === 'ethereum') {
				chainFromLink = 'ethereum'
			} else if (paths[2] === 'base') {
				chainFromLink = 'base'
			} else if (paths[2] === 'matic') {
				chainFromLink = 'polygon'
			}
			contractAddressFromLink = paths[3]
			tokenIdFromLink = paths[4]
		} else if (paths[2] === 'solana') {
			chainFromLink = 'solana'
			tokenAddressFromLink = paths[3]
		} else {
			error = "Sorry, this doesn't appear to be a valid opensea url"
		}
	} else if (url.hostname == 'etherscan.io') {
		chainFromLink = 'ethereum'
		if (paths[1] === 'token') {
			contractAddressFromLink = paths[2]
			tokenIdFromLink = url.searchParams.get('a') || ''
		} else if (paths[1] == 'nft') {
			contractAddressFromLink = paths[2]
			tokenIdFromLink = paths[3]
		} else {
			error = "Sorry, this doesn't appear to be a valid etherscan url"
		}
	} else if (url.hostname == 'basescan.io' || url.hostname === 'basescan.org') {
		chainFromLink = 'base'
		if (paths[1] === 'token') {
			contractAddressFromLink = paths[2]
			tokenIdFromLink = url.searchParams.get('a') || ''
		} else if (paths[1] == 'nft') {
			contractAddressFromLink = paths[2]
			tokenIdFromLink = paths[3]
		} else {
			error = "Sorry, this doesn't appear to be a valid basescan url"
		}
	} else if (url.hostname == 'blur.io') {
		chainFromLink = 'ethereum'
		if (paths[1] !== 'asset') {
			error = "Sorry, this doesn't appear to be a valid blur url"
		} else {
			contractAddressFromLink = paths[2]
			tokenIdFromLink = paths[3]
		}
	} else if (url.hostname == 'zora.co') {
		if (paths[2].includes(':') && paths[3]) {
			const pathTwoSplit = paths[2].split(':')
			chainFromLink = pathTwoSplit[0]
			contractAddressFromLink = pathTwoSplit[1]
			tokenIdFromLink = paths[3]
		} else {
			error = "Sorry, this doesn't appear to be a valid zora url"
		}
	} else if (url.hostname === 'shapescan.xyz') {
		chainFromLink = 'shape'
		if (paths[1] !== 'token' && paths[3] !== 'instance') {
			error = "Sorry, this doesn't appear to be a valid shapescan url"
		} else {
			contractAddressFromLink = paths[2]
			tokenIdFromLink = paths[4]
		}
	} else if (url.hostname === 'highlight.xyz') {
		const chainIndex = paths[2].indexOf(':')
		if (paths[1] !== 'mint' && paths[3] !== 't' && chainIndex <= 0) {
			error = "Sorry, this doesn't appear to be a valid highlight url"
		} else {
			const chainFromPath = paths[2].slice(0, chainIndex)
			if (
				chainFromPath &&
				chainFromPath.length > 0 &&
				chainOptions.find(option => option.slug === chainFromPath)
			) {
				chainFromLink = chainFromPath
				const truncated = paths[2].slice(chainIndex + 1)
				if (truncated.includes(':')) {
					const indexOfChar = truncated.indexOf(':')
					contractAddressFromLink = truncated.slice(0, indexOfChar)
				} else {
					contractAddressFromLink = truncated
				}
				tokenIdFromLink = paths[4]
			} else {
				error = `Sorry we don't support this chain yet: ${chainFromPath}`
			}
		}
	} else {
		error =
			'Invalid URL. Currently, we only accept links to the marketplaces listed below.'
	}
	console.log('Link return vals', {
		contractAddressFromLink,
		tokenIdFromLink,
		tokenAddressFromLink,
		chainFromLink,
		error,
	})
	return {
		contractAddressFromLink,
		tokenIdFromLink,
		tokenAddressFromLink,
		chainFromLink,
		error,
	}
}

function isValidContractAddress(contractAddress: string) {
	if (contractAddress === '') {
		return false
	}
	return true
}

function isValidTokenId(tokenId: string) {
	if (tokenId === '' || isNaN(Number(tokenId))) {
		return false
	}
	return true
}

function isValidTokenAddress(tokenAddress: string) {
	if (tokenAddress === '') {
		return false
	}
	try {
		const isValidAddress = PublicKey.isOnCurve(tokenAddress)
		if (isValidAddress) {
			return true
		} else {
			return false
		}
	} catch (e) {
		return false
	}
}

function isValidURL({ link }: { link: string }) {
	try {
		new URL(link)
	} catch (e) {
		return false
	}
	return true
}

export async function action({ request, params }: ActionFunctionArgs) {
	const form = await request.formData()
	const link = form.get('Search by URL') as string
	let contractAddress = form.get('Contract Address') as string
	let tokenAddress = form.get('Token Address') as string
	let tokenId = form.get('Token ID') as string
	let chainFromForm = form.get('chain') as string
	let chain: string = ''

	let errors = {
		contractAddress: '',
		tokenAddress: '',
		tokenId: '',
		chain: '',
		link: '',
		cid: '',
	}

	// Detect the NFT's chain
	if (link && link !== '') {
		const urlValidity = isValidURL({ link })
		if (!urlValidity) {
			return json({ link: 'Please enter a valid URL or search by token info' })
		}
		const {
			contractAddressFromLink,
			tokenIdFromLink,
			tokenAddressFromLink,
			chainFromLink,
			error,
		} = getStringsFromLink(link)
		if (chainFromLink && !error) {
			chain = chainFromLink
			contractAddress = contractAddressFromLink
			tokenId = tokenIdFromLink
			tokenAddress = tokenAddressFromLink
		} else {
			return json({ link: error })
		}
	} else if (chainFromForm) {
		chain = chainFromForm
	}

	if (chain === 'solana') {
		if (!isValidTokenAddress(tokenAddress)) {
			errors.tokenAddress = `Invalid Token Address - ${tokenAddress}`
		}
	} else {
		if (!isValidContractAddress(contractAddress)) {
			errors.contractAddress = `Invalid Contract Address - ${contractAddress}`
		}
		if (!isValidTokenId(tokenId)) {
			errors.tokenId = `Invalid Token ID - ${tokenId}`
		}
	}

	try {
		const nftData = await getNFTFromCache(
			chain,
			contractAddress,
			chain === 'solana' ? tokenAddress : tokenId,
		)
		if (nftData && (!nftData.success || nftData?.success === true)) {
			return redirect(
				`/nftProfile/${chain}${chain === 'solana' ? '' : `/${contractAddress}`}/${chain === 'solana' ? tokenAddress : tokenId}`,
				{
					status: 302,
				},
			)
		} else {
			if (link) {
				errors.link = 'Please enter a valid URL or search by token info'
			} else {
				if (tokenAddress)
					errors.tokenAddress = `Invalid Token Address - ${tokenAddress}`
				if (contractAddress)
					errors.contractAddress = `Invalid Contract Address - ${contractAddress}`
				if (tokenId) errors.tokenId = `Invalid Token ID - ${tokenId}`
			}
			return json(errors)
		}
	} catch (e) {
		console.error(e)
		return json({
			link: "This NFT doesn't exist or match our schema, please check and try again",
			tokenId:
				"This NFT doesn't exist or match our schema, please check and try again",
			contractAddress:
				"This NFT doesn't exist or match our schema, please check and try again",
			tokenAddress:
				"This NFT doesn't exist or match our schema, please check and try again",
		})
	}
}

export default function index() {
	const { recordSectionAttestations, profileSectionContent, featuredContent } =
		useLoaderData<{
			recordSectionAttestations: string[]
			profileSectionContent: any[]
			featuredContent: any
		}>()

	return (
		<Home
			recordSectionAttestations={recordSectionAttestations}
			profileSectionContent={profileSectionContent}
			featuredContent={featuredContent}
			helpUrl="/guides"
		/>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
